import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import { useFormikContext } from 'formik'
import { makeStyles } from '@mui/styles'
import CancelIcon from '@mui/icons-material/ClearRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'

import lang from '@/lang'
import theme from '@/theme'

import CustomerService from '@/modules/customer/customerService'
import { TextInput } from '@/modules/base/components/inputs/index'
import Loader from '@/modules/base/components/loader/loader'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { SquareIconButton } from '@/modules/base/components/buttons'

import IconMagnifierCheck from '@assets/images/icons/magnifier_check.svg'
import { errorCodes } from '@/modules/base/util/constants'
import { Box, Typography } from '@mui/material'

const useStyles = makeStyles((theme) => ({
  labelWrapper: (props: { readOnly: boolean }) => ({
    cursor: props.readOnly ? 'unset' : 'pointer',
    display: 'flex',
    height: 45,
    width: 80,
    justifyContent: 'center',
    alignItems: 'center',
    border: theme.border,
    borderLeft: 0,
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    position: 'relative',
  }),
  bubbleHeader: {
    marginTop: 0,
  },
  loader: {
    top: '69%',
    height: 44,
  },
  result: {
    background: theme.backgroundColor,
    padding: 20,
    borderRadius: 4,
    border: theme.border,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    lineHeight: '28px',
    '& > span > span > svg': {
      width: 14,
      height: 14,
    },
  },
  wrapper: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  textInput: {
    '& input': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  popperContent: {
    maxWidth: 500,
    minWidth: 300,
    padding: 20,
  },
}))

interface IValidatonResponse {
  body: {
    data: {
      valid: boolean
      validationData: {
        details: { field: string, value: string }[]
        message: string
      }
    }
  }
}

interface IValidatonResult {
  valid: string[]
  invalid: string[]
  notInquired: string[]
  notShared: string[]
  message: string
}

interface IVatNumberInputProps {
  name?: string
  useFormik: boolean
  readOnly?: boolean
  customClass?: string
  client?: { id: string }
  placeholder?: string
  showValidationButton?: boolean
  vatNumber?: string
  onChange?: (value: string, name: string) => void
  address?: { companyName: string, city: string, zipCode: string, street: string }
  customClassLabel?: string
  dataQsId?: string
}

const VatNumberInput = (props: IVatNumberInputProps) => {
  const {
    name = 'vatNumber',
    useFormik,
    customClass,
    readOnly,
    client,
    address,
    placeholder,
    showValidationButton = true,
    onChange,
    customClassLabel,
    dataQsId = 'vat-number-input',
  } = props

  const formikContext = useFormik === undefined || useFormik ? useFormikContext() : null

  const vatNumberField = formikContext?.getFieldMeta('vatNumber') as { value: string }
  const addressField = formikContext?.getFieldMeta('customerDefaultAddress') as { value: { billingAddress: { companyName: string, city: string, zipCode: string, street: string } } }
  const companyName = address?.companyName || addressField?.value?.billingAddress?.companyName
  const city = address?.city || addressField?.value?.billingAddress?.city
  const zipCode = address?.zipCode || addressField?.value?.billingAddress?.zipCode
  const street = address?.street || addressField?.value?.billingAddress?.street

  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<IValidatonResult>()
  const [internalVatNumber, setInternalVatNumber] = useState(props.vatNumber || '')

  useEffect(() => {
    setInternalVatNumber(props.vatNumber || '')
  }, [props.vatNumber])

  const vatNumber = vatNumberField?.value ?? internalVatNumber

  const classes = useStyles({ readOnly: readOnly || false })

  const showResult = (res: IValidatonResponse) => {
    setLoading(false)
    const { body: { data: { validationData, valid } } } = res

    const result = {} as IValidatonResult

    if (validationData) {
      if (valid) {
        const { details } = validationData

        result.valid = details.filter(({ value }) => value === 'valid').map(({ field }) => lang.ustIdVerification[field])
        result.invalid = details.filter(({ value }) => value === 'invalid').map(({ field }) => lang.ustIdVerification[field])
        result.notInquired = details.filter(({ value }) => value === 'notInquired').map(({ field }) => lang.ustIdVerification[field])
        result.notShared = details.filter(({ value }) => value === 'notSharedByEUCountry').map(({ field }) => lang.ustIdVerification[field])
      } else {
        let { message } = validationData
        if (message === 'Mindestens eins der Pflichtfelder ist nicht besetzt.') {
          message = lang.customerVatNumberValidationOwnVatNumberMissingError
        }
        result.message = `${lang.ustIdVerification.error}. ${message}`
      }
    } else if (valid) {
      result.message = lang.customerVatNumberValidSuccessMessage
    } else {
      result.message = lang.customerVatNumberInvalidErrorMessage
    }

    setResult(result)
  }

  const handleLabelClick = async() => {
    if (readOnly) return

    try {
      const response = await CustomerService.validateVatNumber({ vatNumber, companyName, city, zipCode, street }, client?.id)
      showResult(response)
    } catch (error) {
      let errorMessage = lang.customerVatNumberInvalidErrorMessage
      if (error?.body?.meta?.ownVatNumber?.[0]?.code === errorCodes.REQUIRED) {
        errorMessage = lang.customerVatNumberValidationOwnVatNumberMissingError
      }

      toast.showToast({ type: 'error', message: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (value: string, name: string) => {
    if (!formikContext) {
      setInternalVatNumber(value)
    }
    onChange?.(value, name)
  }

  return (
    <div className={clsx(classes.wrapper, customClass)}>
      <TextInput
        dataQsId={dataQsId}
        name={name}
        label={lang.customerVatNumberLabel}
        value={vatNumber || ''}
        customClass={classes.textInput}
        onChange={handleChange}
        useFormik={useFormik}
        readOnly={readOnly}
        enableAiFeatures={false}
        placeholder={placeholder}
        customClassLabel={customClassLabel}
      />
      {showValidationButton && (
        <SquareIconButton
          disabled={readOnly}
          onClick={handleLabelClick}
          className={classes.labelWrapper}
          size='large'
          icon={IconMagnifierCheck}
          variant='white'
          title={lang.generics.inspect}
          popperStyle={{ popperContent: classes.popperContent }}
        >
          <div>
            <Typography marginBottom={1} variant="h6" className={classes.bubbleHeader}>{lang.ustIdVerification.title}</Typography>
            <Box marginBottom={2} fontSize='small'>{lang.ustIdVerification.description.a}<a target='_blank' href='https://www.bzst.de/DE/Unternehmen/Identifikationsnummern/Umsatzsteuer-Identifikationsnummer/eVatR/eVatR_node.html'> USt. ID </a>{lang.ustIdVerification.description.b}</Box>
          </div>
          {loading
            ? <Loader className={classes.loader} />
            : <Result result={result} classes={classes} />}
        </SquareIconButton>
      )}
    </div>
  )
}

const Result = ({ result, classes }) => {
  if (!result) return null
  if (!Object.keys(result).length) return null

  let i = 0

  const getRow = (field, value) => (<span className={classes.row} key={`row#${i++}`}>
    <span>
      {field}
    </span>
    <span>
      {value}
    </span>
  </span>)

  const getTable = () => {
    return [
      getRow(lang.ustIdVerification.columnLeft, lang.ustIdVerification.columnRight),
      ...result.valid.map((field) => getRow(field, <CheckRoundedIcon fontSize='small' htmlColor={theme.green} />)),
      ...result.invalid.map((field) => getRow(field, <CancelIcon fontSize='small' htmlColor={theme.red} />)),
      ...result.notInquired.map((field) => getRow(field, lang.ustIdVerification.notInquired)),
      ...result.notShared.map((field) => getRow(field, lang.ustIdVerification.notShared)),
    ]
  }

  return (
    <div className={classes.result}>
      {result.message
        ? result.message
        : <>
          {getTable()}
        </>}
    </div>
  )
}

export default VatNumberInput
